import type { Kit } from '@repo/types';
import { kitResultsViewable } from '@repo/utils';
import {
  actionPlanSignatureFromKit,
  hasSeenUpdate,
  markUpdateSeen,
  resultsSignatureFromKit,
  type NewUpdateSurface,
} from 'helpers/newUpdateIndicators';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { usePublishedPractitionerNote } from 'services/practitionerNote/usePublishedPractitionerNote';
import { useSystemInfo } from 'services/system-info/useSystemInfo';
import { getKitById, getKitsForTinyAccountId } from 'store/kits';
import { getImpersonateId } from 'store/user';
import { seenIndicatorDB } from 'utils/seenIndicatorDB';

const subscribeToSeenIndicator = seenIndicatorDB.subscribe;
const getSeenIndicatorVersion = (): number => seenIndicatorDB.getVersion();
const getSeenIndicatorVersionServer = (): number => 0;

const useSeenIndicatorVersion = (): number =>
  useSyncExternalStore(
    subscribeToSeenIndicator,
    getSeenIndicatorVersion,
    getSeenIndicatorVersionServer,
  );

export interface NewUpdateState {
  hasNewUpdate: boolean;
  signature: string | null;
}

export interface TinyAccountNewUpdateState {
  anyNewUpdate: boolean;
  anyNonLatestKitHasNewUpdate: boolean;
  perKit: Record<string, { results: boolean }>;
}

const kitResultsSignature = (
  kit: Kit,
  latestSciVersion: string | null | undefined,
): string | null => {
  if (!kit.date_results_ready) return null;
  return resultsSignatureFromKit(kit, latestSciVersion);
};

export const useHasNewUpdate = (
  tinyAccountId: string | null,
  kitId: string | null,
  surface: NewUpdateSurface,
): NewUpdateState => {
  const impersonateId = useSelector(getImpersonateId);
  const kit = useSelector(getKitById(kitId ?? undefined));
  const { latestSciVersion } = useSystemInfo();
  const { data: pracNote } = usePublishedPractitionerNote(
    surface === 'actionPlan' ? kitId : null,
  );
  const seenVersion = useSeenIndicatorVersion();

  return useMemo(() => {
    if (!tinyAccountId || !kitId || !kit) {
      return { hasNewUpdate: false, signature: null };
    }
    if (!kitResultsViewable(kit, !!impersonateId)) {
      return { hasNewUpdate: false, signature: null };
    }
    // latestSciVersion loads via a separate request and is briefly empty on a cold
    // reload; gate on it so we never build (and persist) an incomplete signature.
    if (latestSciVersion == null) {
      return { hasNewUpdate: false, signature: null };
    }

    const signature =
      surface === 'results'
        ? resultsSignatureFromKit(kit, latestSciVersion)
        : actionPlanSignatureFromKit(kit, latestSciVersion, pracNote ?? null);

    const seen = hasSeenUpdate(surface, tinyAccountId, kitId, signature);
    return { hasNewUpdate: !seen, signature };
  }, [
    tinyAccountId,
    kitId,
    kit,
    impersonateId,
    latestSciVersion,
    pracNote,
    surface,
    seenVersion,
  ]);
};

export const useTinyAccountNewUpdateState = (
  tinyAccountId: string | null,
  latestKitId: string | null,
): TinyAccountNewUpdateState => {
  const impersonateId = useSelector(getImpersonateId);
  const kits = useSelector(getKitsForTinyAccountId(tinyAccountId));
  const { latestSciVersion } = useSystemInfo();
  const seenVersion = useSeenIndicatorVersion();

  return useMemo(() => {
    const perKit: Record<string, { results: boolean }> = {};
    let anyNewUpdate = false;
    let anyNonLatestKitHasNewUpdate = false;

    if (!tinyAccountId || !kits) {
      return { anyNewUpdate, anyNonLatestKitHasNewUpdate, perKit };
    }
    // Wait for latestSciVersion before comparing signatures — see useHasNewUpdate.
    if (latestSciVersion == null) {
      return { anyNewUpdate, anyNonLatestKitHasNewUpdate, perKit };
    }

    for (const kit of kits) {
      if (!kit.id) continue;
      if (!kitResultsViewable(kit, !!impersonateId)) {
        perKit[kit.id] = { results: false };
        continue;
      }
      const signature = kitResultsSignature(kit, latestSciVersion);
      if (!signature) {
        perKit[kit.id] = { results: false };
        continue;
      }
      const unseen = !hasSeenUpdate(
        'results',
        tinyAccountId,
        kit.id,
        signature,
      );
      perKit[kit.id] = { results: unseen };
      if (unseen) {
        anyNewUpdate = true;
        if (kit.id !== latestKitId) {
          anyNonLatestKitHasNewUpdate = true;
        }
      }
    }

    return { anyNewUpdate, anyNonLatestKitHasNewUpdate, perKit };
  }, [
    tinyAccountId,
    kits,
    impersonateId,
    latestSciVersion,
    latestKitId,
    seenVersion,
  ]);
};

export const useMarkSurfaceSeen = (
  tinyAccountId: string | null,
  kitId: string | null,
  surface: NewUpdateSurface,
): void => {
  const { signature } = useHasNewUpdate(tinyAccountId, kitId, surface);
  const impersonateId = useSelector(getImpersonateId);

  useEffect(() => {
    if (impersonateId) return;
    if (!tinyAccountId || !kitId || !signature) return;
    markUpdateSeen(surface, tinyAccountId, kitId, signature);
  }, [tinyAccountId, kitId, signature, surface, impersonateId]);
};
